import { StyledTableLink } from '@common/Dependency/styled';
import { statusToIndicator } from '@common/Execution';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { SearchSmIcon, TrashCIcon } from '@prophecy/icons';
import { NestedData } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Button, ColumnsType, Ellipsis, StackItem, Stack, Table, Text, Tooltip } from '@prophecy/ui-v3';
import { formattedDateTime, toFormattedTime } from '@prophecy/utils/date';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { useRunsData } from '../../Workflow/AttachFabric/componentRunhook';
import { PipelineJobRunType } from '../../Workflow/AttachFabric/types';
import { StyledTableWithFooter } from '../config/styled';
import { formatCount } from '../util';

type Props = {
  uid: string;
  entity: Entity;
  isSessionUsed: boolean;
};

const StyledStack = styled(Stack)`
  overflow: hidden;
`;

export default function PipelineRuns({ uid, entity, isSessionUsed }: Props) {
  const {
    pipelineRuns,
    loadMoreData,
    runDataLoading,
    runDataLoadingMessage,
    deletingIdsStatus,
    handleDeleteRunID,
    connected,
    errorMsg
  } = useRunsData({
    entity,
    uid,
    isMetadataRun: true
  });
  const runColumns: ColumnsType<PipelineJobRunType> = useMemo(
    () => [
      {
        key: 'status',
        dataIndex: 'status',
        align: 'center',
        width: '30px',
        render: (status: string) => statusToIndicator(status)
      },
      {
        key: 'jobName',
        dataIndex: 'jobName',
        title: 'Trigger',
        width: '20%',
        sortable: true,
        render: (name: string, record: PipelineJobRunType) => {
          const runType = record.runType;
          if (!name) return runType ? runType + ' Run' : '--';
          const runId = record.pipelineRunId;
          return (
            <StyledTableLink
              to={Private_Routes.Job.home.getUrl({ uid: record.jobUID })}
              data-test-id={`pipeline-runs-job-link-${runId}-button`}>
              <Ellipsis tooltip={true}>
                {name} {runType && <span>( {runType + ' Run'} ) </span>}
              </Ellipsis>
            </StyledTableLink>
          );
        }
      },
      {
        key: 'branch',
        dataIndex: 'branch',
        title: 'Branch',
        sortable: true,
        render: renderColumnWithEllipsis
      },
      {
        key: 'date',
        dataIndex: 'submissionTime',
        width: '20%',
        title: 'Start Time',
        sortable: true,
        render: (submissionTime: number) => {
          return (
            <Tooltip title={formattedDateTime(submissionTime)}>
              <span>{timeAgoFormat(submissionTime)}</span>
            </Tooltip>
          );
        }
      },
      {
        key: 'timeTaken',
        dataIndex: 'timeTaken',
        title: 'Time Taken',
        sortable: true,
        minWidth: 115,
        render: (timeTaken: number) => {
          return renderColumnWithEllipsis(toFormattedTime(timeTaken));
        }
      },
      {
        key: 'rowsRead',
        dataIndex: 'rowsRead',
        align: 'center',
        minWidth: 125,
        title: 'Total rows read',
        sortable: true,
        render: (rows: number) => renderColumnWithEllipsis(formatCount(rows))
      },
      {
        key: 'rowsWritten',
        dataIndex: 'rowsWritten',
        align: 'center',
        title: 'Total rows written',
        minWidth: 140,
        sortable: true,
        render: (rows: number) => renderColumnWithEllipsis(formatCount(rows))
      },
      {
        key: 'actions',
        width: '90px',
        align: 'center',
        dataIndex: 'actions',
        resizable: false,
        render: (_: string, record: NestedData, i: number) => {
          const runId = record.pipelineRunId;
          const isLoading = typeof deletingIdsStatus?.[runId] === 'number';
          return (
            <Stack direction='horizontal' gap={theme.spaces.x12}>
              <Table.IconButton
                icon={<SearchSmIcon type='default' />}
                elementType='link'
                to={getIDEUrl({ uid: record.pipelineUID, entity: Entity.Pipeline }, { 'run-id': runId })}
                aria-label='View pipeline run'
                data-test-id={`pipeline-runs-search-${runId}-button`}
              />
              <Table.IconButton
                onClick={() => handleDeleteRunID(runId, uid, i)}
                loading={isLoading}
                disabled={isLoading || runDataLoading || !connected}
                icon={isLoading ? null : <TrashCIcon type='default' size='xs' />}
                aria-label='Delete run'
                data-test-id={`pipeline-runs-delete-${runId}-button`}
              />
            </Stack>
          );
        }
      }
    ],
    [connected, handleDeleteRunID, runDataLoading, deletingIdsStatus, uid]
  );

  const rows = useMemo(
    () => (pipelineRuns?.rows || []).map((row, i) => ({ ...row, key: row.pipelineRunId + i })),
    [pipelineRuns?.rows]
  );
  return (
    <StyledStack gap={theme.spaces.x40} height='100%'>
      <Stack direction='horizontal' gap={theme.spaces.x12} align='space-between' alignY='center'>
        <Text level='md'>Historical Runs</Text>
      </Stack>
      <StackItem grow='1'>
        <StyledTableWithFooter
          data-test-id='pipeline-runs-table'
          columns={runColumns}
          height='100%'
          showEmptyMessage={{
            description: connected
              ? runDataLoading
                ? runDataLoadingMessage
                : errorMsg || 'No Data'
              : isSessionUsed
                ? 'Recent session connected is already in use. Use another session to connect.'
                : errorMsg || 'Attach cluster to see the pipeline run details'
          }}
          footer={() =>
            pipelineRuns.next_filters ? (
              <Button
                size='s'
                variant='secondaryGray'
                disabled={runDataLoading}
                loading={runDataLoading && connected}
                onClick={() => loadMoreData()}
                data-test-id='pipeline-runs-view-more-button'>
                View More
              </Button>
            ) : null
          }
          virtualize={true}
          dataSource={rows}
          fixedHeader={true}
        />
      </StackItem>
    </StyledStack>
  );
}
